import { FunctionComponent, useContext, useMemo, useState } from 'react'
import { Box, Checkbox, Chip, FormControl, FormHelperText, InputLabel, ListItemText, MenuItem, OutlinedInput, Select, SelectChangeEvent, Typography } from '@mui/material';
import { ReportPeriodContext } from '../../context/report-periods/report-periods';
import { DialogWrapper } from '../presentation-components/display-elements-components/dialog-wrapper';

export const CloneAssumptionDialog: FunctionComponent<{
  open: boolean,
  isSubmitting: boolean,
  successHandler: (reportPeriodIds: string[]) => void,
  cancelHandler: () => void,
}> = ({
  open,
  isSubmitting,
  successHandler,
  cancelHandler,
}) => {
    //Context
    const reportPeriodContext = useContext(ReportPeriodContext)
    //State
    const [selectedReportPeriodIds, setSelectedReportPeriodIds] = useState<string[]>([])
    const [error, setError] = useState<string>()

    // Periods the assumption can be cloned into: open (unlocked) periods other than the current one.
    const targetPeriods = useMemo(
      () => reportPeriodContext.openReportPeriods.filter(period => period.id !== reportPeriodContext.lastSelectedReportId),
      [reportPeriodContext.openReportPeriods, reportPeriodContext.lastSelectedReportId]
    )

    const selectHandler = (e: SelectChangeEvent<string[]>) => {
      const { value } = e.target;
      setSelectedReportPeriodIds(typeof value === 'string' ? value.split(',') : value);
      setError(undefined)
    }

    const submitHandler = () => {
      if (selectedReportPeriodIds.length === 0) {
        setError('Please select at least one report period')
        return
      }
      successHandler(selectedReportPeriodIds)
    }

    const closeHandler = () => {
      setSelectedReportPeriodIds([])
      setError(undefined)
      cancelHandler()
    }

    return (
      <DialogWrapper open={open} onClose={closeHandler} title='Clone Assumption' cancelHandler={closeHandler} submitHandler={submitHandler} isSubmitting={isSubmitting}>
        <Typography sx={{ mt: 1 }}>Select the report period(s) to copy this assumption into. A new assumption will be created in each selected period.</Typography>
        <FormControl fullWidth variant='outlined' sx={{ mt: 2 }}>
          <InputLabel htmlFor='reportPeriods'>Report Periods</InputLabel>
          <Select id='reportPeriods' name='reportPeriods' label='Report Periods'
            value={selectedReportPeriodIds}
            onChange={selectHandler}
            error={Boolean(error)}
            multiple
            renderValue={(selected) => (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {selected.map((value, index) => {
                  const selectedName = targetPeriods.find(period => period.id === value)?.friendlyName
                  return (
                    <Chip key={index} label={selectedName} />
                  )
                })}
              </Box>
            )} input={<OutlinedInput label='Report Periods' />}>
            {targetPeriods.map((period) => (
              <MenuItem key={period.id} value={period.id}>
                <Checkbox checked={selectedReportPeriodIds.includes(period.id)} />
                <ListItemText primary={period.friendlyName} />
              </MenuItem>
            ))}
          </Select>
          {error &&
            <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>{error}</FormHelperText>
          }
        </FormControl>
      </DialogWrapper>
    )
  }
